import React from "react"
import Header from "../Header"
import Footer from "../Footer"
import WhatsAppFloat from "../WhatsAppFloat"
import { Shield, Eye, Lock, Users, Settings, FileText, Mail, Globe, Building } from "lucide-react"

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-black">
      <Header />

      <main className="pt-20 md:pt-32 pb-8 md:pb-16">
        <div className="w-full px-3 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-8 md:mb-12">
              <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-green-100 rounded-full mb-4 md:mb-6">
                <Shield className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Privacy Policy
              </h1>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
                Your privacy is important to us. Learn how we protect and handle your information.
              </p>
              <div className="mt-4 md:mt-6 inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span>Effective Date: 10/31/25</span>
              </div>
            </div>

            {/* Introduction */}
            <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-8 mb-6 md:mb-8 shadow-sm">
              <p className="text-base md:text-lg text-gray-800 leading-relaxed">
                TrackFam ("TrackFam," "we," "our," or "us") respects your privacy and is committed to protecting it. This Privacy Policy explains how we collect, use, share, and protect personal information when you use our mobile application, services, or otherwise interact with us. By using TrackFam, you consent to the practices described in this policy.
              </p>
            </div>

            {/* Information Collection */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">1. Information We Collect</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4">We collect only the information necessary to provide our location tracking services and operate our business. This may include:</p>
                <ul className="space-y-2 md:space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Contact information (e.g., name, email, phone number) when you create an account or contact us for support.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Location data (GPS, Wi-Fi, and cellular signals) to provide real-time family tracking services.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Device information including battery status, device type, operating system, and mobile network information.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Usage data such as app features accessed, place alerts configured, and family member connections.</span>
                  </li>
                </ul>
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-xs md:text-sm">
                    <strong>Important:</strong> Location data is collected only with your explicit consent and can be disabled at any time through your device settings. All location data is encrypted end-to-end. We do not knowingly collect information from children under 13 without parental consent.
                  </p>
                </div>
              </div>
            </section>

            {/* How We Use Information */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4">We use personal information to:</p>
                <ul className="space-y-2 md:space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Provide real-time location tracking and family safety services.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Send place alerts and battery status notifications to authorized family members.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Respond to support inquiries and improve customer service.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Analyze app usage to improve features and user experience.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Prevent unauthorized access, fraud, and ensure platform security.</span>
                  </li>
                </ul>
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-purple-800 text-xs md:text-sm">
                    <strong>Location Privacy:</strong> Your location data is only shared with family members you explicitly authorize. We do not use location data for advertising purposes or share it with third parties. Location tracking can be paused or disabled at any time.
                  </p>
                </div>
              </div>
            </section>

            {/* Sharing and Disclosure */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">3. Sharing and Disclosure</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4">We do not sell or share your location data or personal information with third parties for marketing purposes. We may share information with:</p>
                <ul className="space-y-2 md:space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Family members you explicitly authorize to view your location and receive alerts.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Service providers that help us operate (e.g., cloud hosting, analytics, payment processors) under strict confidentiality agreements.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Law enforcement or regulatory authorities when required by law or to protect safety.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Successor entities in connection with a merger or acquisition, with advance notification to users.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Cookies and Analytics */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">4. Cookies and Analytics</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4 text-sm md:text-base">
                  Our mobile application uses local storage and similar technologies to remember your preferences and improve app performance. We may use analytics services to collect aggregate, non-personal information about app usage and performance.
                </p>
                <p className="text-gray-800 text-sm md:text-base">
                  We do not use cookies or tracking technologies for advertising purposes. Analytics data is anonymized and used solely to improve the TrackFam experience.
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">5. Data Retention</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 text-sm md:text-base">
                  We retain location history and personal information only as long as necessary to provide our services, comply with legal obligations, or resolve disputes. You can delete your location history at any time through the app settings. When you delete your account, all associated location data and personal information will be permanently deleted within 30 days.
                </p>
              </div>
            </section>

            {/* Security */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">6. Security</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 text-sm md:text-base">
                  We implement industry-standard security measures including end-to-end encryption for location data, secure data transmission protocols, and encrypted storage. We use administrative, technical, and physical safeguards to protect your information from unauthorized access, theft, or loss. However, no method of transmission or electronic storage is 100% secure, so we cannot guarantee absolute security.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">7. Your Rights and Choices</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4 text-sm md:text-base">Depending on your location, you may have certain rights regarding your personal information, including the right to:</p>
                <ul className="space-y-2 md:space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Access or request a copy of the personal data we hold.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Correct or update your information.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Request deletion or object to certain uses of your data.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Withdraw consent (where processing is based on consent).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-800 text-sm md:text-base">Opt out of marketing communications at any time.</span>
                  </li>
                </ul>
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-green-800 text-xs md:text-sm">
                    To exercise any of these rights, please contact us using the information below.
                  </p>
                </div>
              </div>
            </section>

            {/* Changes to Policy */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">8. Changes to This Policy</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 text-sm md:text-base">
                  We may update this Privacy Policy from time to time. We will notify you of any significant changes through the app and by updating the "Effective Date." Your continued use of TrackFam after updates constitutes acceptance of the revised policy.
                </p>
              </div>
            </section>

            {/* Contact Us */}
            <section className="mb-6 md:mb-10">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">9. Contact Us</h2>
              </div>
              <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
                <p className="text-gray-800 mb-3 md:mb-4 text-sm md:text-base">If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:</p>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center gap-3 p-2 md:p-3 bg-blue-50 rounded-lg">
                    <Mail className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                    <span className="text-blue-800 font-medium text-sm md:text-base">support@trackfam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 md:p-3 bg-green-50 rounded-lg">
                    <Globe className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                    <span className="text-green-800 font-medium text-sm md:text-base">https://www.trackfam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 md:p-3 bg-purple-50 rounded-lg">
                    <Building className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                    <span className="text-purple-800 font-medium text-sm md:text-base">Worldwide Service</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer Note */}
            <div className="text-center mt-8 md:mt-12 p-4 md:p-6 bg-gray-50 rounded-xl md:rounded-2xl border border-gray-200">
              <p className="text-gray-600 text-xs md:text-sm">
                Thank you for trusting TrackFam with your family's safety and privacy. We're committed to protecting your location data and being transparent about our practices.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  )
}
